// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';

import { useMatch } from 'react-router-dom';

// $FlowFixMe[missing-export] exposed only for TS or js
import { selectHelpdeskNewRequestUrl } from '@gandi/authentication';
import { useTranslator, mapTags } from '@gandi/translate';
import { Base, Button } from '@gandi/ui';

import { withRouter } from 'src/helpers/withRouter';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { stopSSE } from 'src/modules/app/sse';

import AlteredSearchWarning from '../../DomainCreate/components/AlteredSearchWarning';
import DomainInput from '../../DomainCreate/components/DomainInput';

import {
  change as setFilter,
  reset as resetFilter,
} from '../../DomainCreate/modules/actions/filters';
import {
  set as setPagination,
  reset as resetPagination,
} from '../../DomainCreate/modules/actions/pagination';
import { reset as resetResults } from '../../DomainCreate/modules/actions/results';
import {
  set as setSearch,
  reset as resetSearch,
  setRequiredAvailables,
} from '../../DomainCreate/modules/actions/search';
import {
  submitSearch,
  loadMoreSuggestions,
} from '../../DomainCreate/modules/actions/suggestions';
import { selectCanLoadMore } from '../../DomainCreate/modules/selectors/pagination';
import {
  selectNonProvided,
  selectFilteredList,
} from '../../DomainCreate/modules/selectors/results';
import {
  selectSearchValue,
  selectSearchError,
  selectAlteredSearch,
  selectSearchLoading,
} from '../../DomainCreate/modules/selectors/search';

import type { ExtendedSuggestion } from '../../DomainCreate/types';
import type {
  PreFetchArgs,
  PreFetchReturn,
  WithRouterProps,
} from '../../types';
import type { OneClickQuery } from '../../types/query';

import { DomainList } from './DomainList';

export type Query = {
  search?: string,
};
type InjectedProps = {
  ...WithRouterProps<{}, OneClickQuery>,
};

type OwnProps = {
  handleChooseDomain: (value: ExtendedSuggestion) => mixed,
};

type Props = {
  ...InjectedProps,
  ...OwnProps,
};

const inputId = 'search';

function DomainSearch({
  location,
  handleChooseDomain,
}: Props): React.MixedElement {
  const { __ } = useTranslator();
  const supportURL = useAppSelector(selectHelpdeskNewRequestUrl);
  const hasResults = useAppSelector(selectNonProvided).length > 0;
  const search = useAppSelector(selectSearchValue);
  const error = useAppSelector(selectSearchError);
  const alteredSearch = useAppSelector(selectAlteredSearch);
  const searchLoading = useAppSelector(selectSearchLoading);
  const canLoadMore = useAppSelector(selectCanLoadMore);

  const studioPage = Boolean(useMatch('/studio/:vhost/convert/:cartId?'));

  const hasPagination = !!search && canLoadMore;

  const suggestions = useAppSelector(state =>
    // $FlowFixMe[incompatible-call]
    selectFilteredList(state, 'packhosting'),
  );

  const showLoadMoreButton = hasPagination && suggestions.length > 0;

  const [selectedDomain, setSelectedDomain] = React.useState();

  const dispatch = useDispatch();

  const loadMore = (): mixed => {
    dispatch(loadMoreSuggestions());
  };

  React.useEffect(() => {
    // we save the this state on mount to keep the info box whatever cart change
    // to avoid the content to move up when adding the free .fr domain
    if (location.query?.search != null) {
      if (location.query.search === search && hasResults) {
        return;
      }

      // $FlowFixMe[not-an-array]
      dispatch(setSearch(location.query.search));

      // $FlowFixMe[not-an-array]
      dispatch(resetResults());
      dispatch(submitSearch(search));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, location.query.search, search]);

  React.useEffect(() => {
    // $FlowFixMe[not-an-array]
    dispatch(setRequiredAvailables(5));
    // $FlowFixMe[not-an-array]
    dispatch(setFilter({ name: 'filter_list', value: 'packhosting' }));
    // $FlowFixMe[not-an-array]
    dispatch(setPagination({ per_page: 5 }));
    return () => {
      stopSSE();
      // $FlowFixMe[not-an-array]
      dispatch(resetFilter());
      // $FlowFixMe[not-an-array]
      dispatch(resetSearch());
      // $FlowFixMe[not-an-array]
      dispatch(resetPagination());
      // $FlowFixMe[not-an-array]
      dispatch(resetResults());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleDomainSelect(domain: ExtendedSuggestion | void): void {
    if (!domain) return;
    // $FlowIgnore[incompatible-call]
    setSelectedDomain(domain.fqdn);
    handleChooseDomain(domain);
  }

  return (
    <Base flex flexCol gap="base">
      <div>
        <DomainInput
          id={inputId}
          process="create"
          hideBulk
          hideOptions
          placeholder={
            studioPage
              ? __('Search a domain name')
              : __('Enter a word or a domain name')
          }
          label={__('Domain name of your choice')}
          isStudioPage={studioPage}
        />

        {alteredSearch &&
          alteredSearch.changes.map(change => (
            <AlteredSearchWarning change={change} withBackground />
          ))}

        {error && (
          <Base backgroundColor="surface" padding="small">
            {mapTags(
              __(
                'Something seems broken right now. Please retry or [[supportLink:]]',
              ),
              () => ({
                supportLink: (
                  <a href={supportURL}>{__('contact the Customer Care')}.</a>
                ),
              }),
            )}
          </Base>
        )}
      </div>

      <DomainList
        selectedDomain={selectedDomain}
        setDomain={handleDomainSelect}
        provided
      />

      <DomainList
        selectedDomain={selectedDomain}
        setDomain={handleDomainSelect}
        provided={false}
      />

      {showLoadMoreButton && (
        <Base flex flexJustifyCenter>
          <Button
            kind="link"
            type="button"
            onClick={loadMore}
            disabled={searchLoading}
          >
            {__('Load more domains')}
          </Button>
        </Base>
      )}
    </Base>
  );
}

DomainSearch.preFetch = ({
  query,
  dispatch,
}: PreFetchArgs<{}, void, Query>): Promise<void> | PreFetchReturn<> => {
  const { search } = query;

  if (search) {
    return Promise.allSettled([
      // $FlowFixMe[not-an-array]
      dispatch(setRequiredAvailables(5)),
      // $FlowFixMe[not-an-array]
      dispatch(setFilter({ name: 'filter_list', value: 'wp1click' })),
      // $FlowFixMe[not-an-array]
      dispatch(setPagination({ per_page: 5 })),
      // $FlowFixMe[not-an-array]
      dispatch(setSearch(search)),
    ]);
  }

  return Promise.resolve();
};

export default (withRouter(DomainSearch): React.ComponentType<OwnProps>);
